import { NodeAuditStyles } from '@flowx/designer/modules/bpmn-editor/bpmn-graph.constants'
import { Point } from '@flowx/designer/modules/bpmn-editor/dictionary/geometry.types'
import {
  MisconfigCount,
  ScriptingLanguage,
} from '@flowx/designer/modules/core/dictionary/common.types'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { AuditUser } from '@flowx/designer/modules/core/dictionary/versioning.dictionary'
import {
  ActionHandler,
  ActionParamType,
} from '@flowx/designer/modules/node-actions/components/action-edit/action-edit.dictionary'
import { ProxyApiAction } from '@flowx/designer/shared/entities/application.entity'
import { ErrorBoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/error-boundary-event.entity'
import { MessageBoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/message-boundary-event.entity'
import {
  TimerBoundaryEvent,
  TimerState,
} from '@flowx/designer/shared/entities/boundary-event/timer-boundary-event.entity'
import { AvailablePlatforms } from '@flowx/designer/shared/entities/misconfig.entity'
import { NodeType } from '@flowx/designer/shared/entities/node.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { ShapeLayoutOptions, SwimlaneSummary } from './swimlane.dictionary'

export type NodeDefinition = {
  id: number
  flowxUuid: string
  name: string
  type: NodeType
  processId?: number
  canGoBack: boolean
  manual: boolean
  stageResourceDefinitionId: string
  stageApplicationId: string
  swimlaneDefinitionId: string
  updateTask?: boolean
  updateSearch?: boolean
  actionDefinitions?: ActionDefinition[]
  incomingSequences?: SequencePoints[]
  nodeRule?: NodeRulesDefinition
  nodeValues?: NodeValue[]
  dataStreams?: NodeDataStream[]
  outgoingSequences?: SequencePoints[]
  paramDefinitions?: ParamDefinition[]
  messageEvents: MessageBoundaryEvent[]
  timerEvents: TimerBoundaryEvent[]
  errorEvents: ErrorBoundaryEvent[]
  isNavAreaAssigned: boolean
  layoutOptions: NodeLayoutOptions
  navigationAreaId: number
  extraData?: {
    markedForSave: boolean
  }
  allowReadOnly?: boolean | null // true means is visible in readonly

  flxStyles?: NodeAuditStyles
}

export type CopyNodesResponse = {
  childUiTemplatesByNode: Record<string, TemplateConfigInterface[]>
  nextNodesByNode: string[]
  nodeDefinitions: NodeDefinition[]
  sequencesForNode: CopyNodesSequencesForNode
  uiTemplatesByNode: TemplateConfigInterface[]
}

export type CopyNodesSequencesForNode = {
  [key: string]: {
    flowxUuid: string
    nextNodeUUID: string
    portType: string
    portUUID: string
    layoutOptions: Point[]
  }[]
}

export type NodeDefinitionNormalized = Omit<
  NodeDefinition,
  | 'messageEvents'
  | 'timerEvents'
  | 'errorEvents'
  | 'actionDefinitions'
  | 'incomingSequences'
  | 'outgoingSequences'
> & {
  messageEvents: number[]
  timerEvents: number[]
  errorEvents: number[]
  incomingSequences: number[]
  outgoingSequences: number[]
  actionDefinitions: number[]
}

export type NewNodeDefinition = Pick<
  NodeDefinition,
  'name' | 'type' | 'processId' | 'canGoBack' | 'swimlaneDefinitionId' | 'layoutOptions'
>

export enum ProcessState {
  WORK_IN_PROGRESS = 'WORK_IN_PROGRESS',
  COMMITTED = 'COMMITTED',
}

export type SensitiveData = {
  field: string
  type: SensitiveDataType
}

export type ProcessPublishedInfo = {
  name: string
  flowxUuid: string
  hasPublishedVersion: boolean
  label?: string
}

export enum SensitiveDataType {
  OBJECT = 'OBJECT',
  LIST = 'LIST',
}

export type ProcessCreateBody = {
  name: string
  applicationId: number
  defaultSwimlaneLayoutOptions: ShapeLayoutOptions
}

export type ProjectProcessCreateBody = {
  resourceName: string
  action: ProxyApiAction
  payload: {
    name: string
    appVersionId: string
    defaultSwimlaneLayoutOptions: SwimlaneSummary['layoutOptions']
    availableOnPlatform: AvailablePlatforms
  }
}

export type ProcessStartTimerEvent = {
  id: number
  flowxUuid: string
  nodeId: NodeDefinition['id']
  processDefinitionVersionId: ProcessDefinition['id']
  timerState: TimerState
}

export type ProcessDefinition = {
  id: number
  resourceId: string
  resourceName: string
  deleted: boolean
  processFavorite: boolean
  description: string
  state: ProcessState
  startTimerEvent: ProcessStartTimerEvent
  nodeDefinitions: NodeDefinition[]
  embeddedSubprocessNodeDefinitions?: NodeDefinition[]
  applicationId: number
  expiryTime: string
  generalData: any
  keyIdentifiers: string[]
  baseUrl: string
  useInReporting: boolean
  reportingDataUpdated: boolean
  createdDate: string
  createdBy: AuditUser
  modifiedDate: string
  modifiedBy: AuditUser
  sensitiveData: SensitiveData[]
  createTask: boolean
  originalCreationTimestamp: string
  messagesCount: MisconfigCount
  availableOnPlatform: AvailablePlatforms
  tasksProcessData: ProcessSettingsViewColumn[]
  swimlanes: SwimlaneSummary[]
  resourceDefinitionId: string
  allowReadOnly: boolean
}

export type NodeDefinitionPatchApi = Omit<
  NodeDefinition,
  | 'messageEvents'
  | 'manual'
  | 'timerEvents'
  | 'errorEvents'
  | 'layoutOptions'
  | 'swimlaneDefinitionId'
  | 'navigationAreaId'
  | 'isNavAreaAssigned'
  | 'extraData'
  | 'processDefinitionVersionId'
  | 'flowxUuid'
>

export type MessageEventNode = NodeDefinitionPatchApi & {
  messageEvent: MessageBoundaryEvent
}

export type TimerEventNode = NodeDefinitionPatchApi & {
  timerEvent: TimerBoundaryEvent
}

export type EventNode = MessageEventNode | TimerEventNode

export type NodeLayoutOptions = Point

export type NodePosition = {
  id: number
  layoutOptions: NodeLayoutOptions
  swimlaneDefinitionId: string
  isNavAreaAssigned?: boolean
  extraData?: {
    markedForSave: boolean
  }
}

export type ActionDefinition = {
  id?: number
  nodeDefinitionId: number
  name: string
  flowxUuid: string
  parentFlowxUuid: string
  order: number
  callbackActions: ActionDefinition[] | null
  handlerService: ActionHandler
  mandatory: boolean
  autoRunChildren: boolean
  automatic: boolean
  manual: boolean
  repeatable: boolean
  optional: boolean

  allowTokenReset: boolean
  restartFromSnapshot: boolean
  keysForRestart: string[]

  actionParamsMap: Record<string, ActionParam>
  actionRuleDefinitions: ActionRule[]
  keys: string[]

  enableDataMapping: boolean
}

export type ActionParam = {
  id?: number
  actionDefinitionId?: number
  key: ActionParamType
  value: string | boolean
  replaceValues: boolean
  dataModelReferencePath?: string
  workflowApplicationId?: string
}

export type ActionRule = {
  id?: number
  flowxUuid?: string
  actionDefinitionId?: number
  expression: string
  language: ScriptingLanguage
  appliesOnCollection: string
  usesFlattenParams?: boolean
  supportsGraalVM?: boolean
  useGraalVM?: boolean
  businessRuleReferences?: ReusableFunctionReference[]
}

export type ReusableFunctionReference = {
  resourceDefinitionId: string
  appId: string
}

export type NodeRulesDefinition = {
  rules: NodeRule[]
  language: ScriptingLanguage
  id?: number
  nodeDefinitionId?: number
  expression: string
  supportsGraalVM?: boolean
  useGraalVM?: boolean
}

export type NodeRule = {
  type: NodeRuleType
  expression: string
  nextNodeFlowxUuid: string
  order: number
}

export type NodeValue = {
  key: NodeValueKey | string
  value: string
  id?: number
  nodeDefinitionId?: number
}

export type NodeDataStream = {
  id: number
  flowxUuid: string
  nodeDefinitionId: number
  type: NodeDataStreamType
  keyName?: string // only for CUSTOM type or WORKFLOW type with enableDataMapping false
  // For WORKFLOW type
  enableDataMapping?: boolean
  resourceDefinitionId?: string
  appId: string
  // For CUSTOM type
  topicName?: string

  refPath?: string
  tempUuid?: string
}

export type NodeDataStreamPayload = {
  added: Omit<NodeDataStream & { tempUuid?: string }, 'id' | 'flowxUuid'>[]
  updated: NodeDataStream[]
  deletedIds: number[]
}

export enum NodeDataStreamType {
  WORKFLOW = 'WORKFLOW',
  CUSTOM = 'CUSTOM',
}

export type ParamDefinition = object

export enum NodeRuleType {
  IF = 'if',
  ELSE_IF = 'else if',
  ELSE = 'else',
}

export enum NodeValueKey {
  TOPIC_NAME = 'topicName',
  INTEGRATION_SCENARIO = 'integrationScenario',
  WORKFLOW = 'workflow',
  KEY_NAME = 'keyName',
}

export const configurationTypes = {
  CUSTOM: { label: 'Custom', value: 'CUSTOM' },
  INTEGRATION: {
    label: 'From custom integration',
    value: 'INTEGRATION',
  },
}

export const nodeValuesConfigurationTypes = {
  WORKFLOW: {
    label: 'From Workflow',
    value: 'WORKFLOW',
  },
  CUSTOM: { label: 'Custom', value: 'CUSTOM' },
  // TODO: This should be added back when Integration Management is under application(post 4.5)
  // INTEGRATION: {
  //   label: 'From custom integration',
  //   value: 'INTEGRATION',
  // },
}

export interface ProcessSettingsViewColumn {
  dataModelType: DataModelAttributeType
  dataModelKey: string
  label: string
  enumData?: ProcessSettingsViewColumnEnumData
}

export interface ProcessSettingsViewColumnEnumData {
  name: string
  resourceDefinitionId: string
}
